import { useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  Image,
  Modal,
  StyleSheet,
  View,
  Alert,
  ActivityIndicator,
  Text,
  Platform,
  TouchableOpacity,
} from "react-native";
import { WebView } from "react-native-webview";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../store";
import { env } from "../../../../../utils/env";

interface IProps {
  width: number;
  gamePath: string;
  onClose: VoidFunction;
}

function MiniGameModal({ width, gamePath, onClose }: IProps) {
  const isSmall = useMemo(() => width <= 1024, [width]);
  const opacity = useRef(new Animated.Value(0)).current;
  const webViewRef = useRef<WebView>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const iframeCleanupRef = useRef<VoidFunction | null>(null);
  const webCloseTimeoutRef = useRef<number | null>(null);

  // Get auth token from Redux
  const auth = useSelector((state: RootState) => state.auth);
  const token = auth.token;
  const langCode = useSelector((state: RootState) => state.i18n.langCode);
  const platform = Platform.OS;
  useEffect(() => {
    Animated.timing(opacity, {
      toValue: 1,
      duration: 200,
      useNativeDriver: true,
    }).start();
  }, []);

  useEffect(() => {
    return () => {
      if (iframeCleanupRef.current) {
        iframeCleanupRef.current();
        iframeCleanupRef.current = null;
      }
      if (webCloseTimeoutRef.current !== null) {
        window.clearTimeout(webCloseTimeoutRef.current);
        webCloseTimeoutRef.current = null;
      }
    };
  }, []);

  const handleClose = () => {
    Animated.timing(opacity, {
      toValue: 0,
      duration: 200,
      useNativeDriver: true,
    }).start(onClose);
  };

  const bindWebIframeCloseBridge = () => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return;

    try {
      const onPointerDown = (event: PointerEvent | MouseEvent) => {
        const viewportW = iframeWindow.innerWidth || 0;
        const x = event.clientX || 0;
        const y = event.clientY || 0;
        const isTopRightCloseHit =
          x >= viewportW - 120 && x <= viewportW && y <= 120;

        if (isTopRightCloseHit) {
          if (webCloseTimeoutRef.current !== null) return;
          webCloseTimeoutRef.current = window.setTimeout(() => {
            webCloseTimeoutRef.current = null;
            handleClose();
          }, 220);
        }
      };

      iframeWindow.addEventListener("pointerdown", onPointerDown, true);
      iframeWindow.addEventListener("mousedown", onPointerDown, true);

      iframeCleanupRef.current = () => {
        iframeWindow.removeEventListener("pointerdown", onPointerDown, true);
        iframeWindow.removeEventListener("mousedown", onPointerDown, true);
      };
    } catch (error) {
      iframeCleanupRef.current = null;
    }
  };

  // Use environment variables for URLs
  const WEB_FRONTEND_URL =
    platform === "web"
      ? window.location.origin
      : env("SVARA_WEB_URL") || "https://t-svara.bowa.awd-group.tech";
  const API_URL = env("SVARA_API_URL") || "https://t-sapi.bowa.awd-group.tech";

  // Extract just the game path without query params
  const gamePathOnly = gamePath.split("?")[0];

  // Build the URL with the current user's token
  const gameUrl = `${WEB_FRONTEND_URL}/miniGames${gamePathOnly}?gameApiUrl=${API_URL}/api/game&token=${token}&language=${langCode || "en"}`;

  // console.log("🎮 Mini Game URL:", gameUrl);

  // Minimal injected JavaScript for debugging
  const injectedJavaScript = `
    (function() {
      console.log('Mini game initialized on ${Platform.OS}');
      console.log('URL:', window.location.href);
      
      // Forward console logs to React Native
      const originalLog = console.log;
      const originalError = console.error;
      
      console.log = function(...args) {
        originalLog.apply(console, args);
        try {
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(JSON.stringify({
              type: 'console.log',
              data: args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg))
            }));
          }
        } catch(e) {}
      };
      
      console.error = function(...args) {
        originalError.apply(console, args);
        try {
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(JSON.stringify({
              type: 'console.error',
              data: args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg))
            }));
          }
        } catch(e) {}
      };
      
      // Global error handler
      window.addEventListener('error', function(e) {
        console.error('Global error:', e.message);
        try {
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(JSON.stringify({
              type: 'error',
              data: { message: e.message, filename: e.filename, line: e.lineno }
            }));
          }
        } catch(err) {}
      });

      // Bridge top-right in-game close click to native host
      const sendCloseIntent = function() {
        try {
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(JSON.stringify({
              type: 'close_minigame_intent'
            }));
          }
        } catch (e) {}
      };

      const onGamePointerDown = function(evt) {
        const x = (evt && typeof evt.clientX === 'number') ? evt.clientX : 0;
        const y = (evt && typeof evt.clientY === 'number') ? evt.clientY : 0;
        const w = window.innerWidth || 0;
        if (x >= (w - 120) && x <= w && y <= 120) {
          setTimeout(sendCloseIntent, 220);
        }
      };
      window.addEventListener('pointerdown', onGamePointerDown, true);
      window.addEventListener('mousedown', onGamePointerDown, true);
      
      console.log('Mini game bridge ready');
    })();
    true;
  `;

  // Handle messages from WebView
  const handleWebViewMessage = (event: any) => {
    try {
      const message = JSON.parse(event.nativeEvent.data);

      switch (message.type) {
        case "close_minigame_intent":
        case "close":
        case "close_minigame":
        case "exit":
          if (webCloseTimeoutRef.current === null) {
            webCloseTimeoutRef.current = window.setTimeout(() => {
              webCloseTimeoutRef.current = null;
              handleClose();
            }, 0);
          }
          break;
        case "console.log":
          console.log("🌐 [Game]:", ...message.data);
          break;
        case "console.error":
          console.error("🌐 [Game Error]:", ...message.data);
          break;
        case "error":
          console.error("🌐 [JS Error]:", message.data);
          break;
        default:
          console.log("🌐 [Game Message]:", message);
      }
    } catch (error) {
      console.log("🌐 [Raw]:", event.nativeEvent.data);
    }
  };

  // Check if we have a token
  if (!token) {
    return (
      <Modal animationType="none" transparent visible>
        <Animated.View style={{ ...styles.overlay, opacity }}>
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>No authentication token found</Text>
            <TouchableOpacity onPress={handleClose} style={styles.errorButton}>
              <Text style={styles.errorButtonText}>Close</Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </Modal>
    );
  }

  return (
    <Modal animationType="none" transparent visible>
      <Animated.View style={{ ...styles.overlay, opacity }}>
        <View
          style={[
            styles[isSmall ? "containerSmallScreen" : "containerBigScreen"],
          ]}
        >
          {platform !== "web" ? (
            <WebView
              ref={webViewRef}
              source={{ uri: gameUrl }}
              style={styles.webview}
              javaScriptEnabled={true}
              domStorageEnabled={true}
              allowsInlineMediaPlayback={true}
              mediaPlaybackRequiresUserAction={false}
              startInLoadingState={true}
              scalesPageToFit={true}
              mixedContentMode="always"
              originWhitelist={["*"]}
              injectedJavaScript={injectedJavaScript}
              onMessage={handleWebViewMessage}
              onError={(syntheticEvent) => {
                const { nativeEvent } = syntheticEvent;
                console.error("❌ WebView error:", nativeEvent);
              }}
              onHttpError={(syntheticEvent) => {
                const { nativeEvent } = syntheticEvent;
                console.error(
                  "❌ HTTP error:",
                  nativeEvent.statusCode,
                  nativeEvent.url,
                );
              }}
              onLoad={() => {
                console.log("✅ Mini game loaded");
              }}
              onLoadStart={() => {
                console.log("⏳ Loading mini game...");
              }}
              onLoadEnd={() => {
                console.log("🏁 Mini game load completed");
              }}
              renderLoading={() => (
                <View style={styles.loadingOverlay}>
                  <ActivityIndicator size="large" color="#f8d955" />
                  <Text style={styles.loadingText}>Loading game...</Text>
                </View>
              )}
            />
          ) : (
            <iframe
              ref={iframeRef}
              src={gameUrl}
              onLoad={bindWebIframeCloseBridge}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
              allow="fullscreen; autoplay; microphone; camera"
            />
          )}
        </View>
      </Animated.View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.8)",
  },
  containerBigScreen: {
    position: "relative",
    width: "70%",
    height: "70%",
    maxWidth: "100%",
    borderColor: "#f8d955",
    borderWidth: 4,
    borderRadius: 28,
    backgroundColor: "#540f09",
    overflow: "hidden",
  },
  containerSmallScreen: {
    position: "relative",
    width: "100%",
    height: "100%",
    maxWidth: "100%",
    maxHeight: "100%",
    backgroundColor: "#540f09",
  },
  webview: {
    flex: 1,
    backgroundColor: "transparent",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#540f09",
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    color: "#f8d955",
    fontSize: 18,
    fontWeight: "bold",
    marginTop: 10,
  },
  errorContainer: {
    backgroundColor: "#540f09",
    padding: 30,
    borderRadius: 20,
    borderColor: "#f8d955",
    borderWidth: 2,
    alignItems: "center",
  },
  errorText: {
    color: "#fff",
    fontSize: 16,
    marginBottom: 20,
  },
  errorButton: {
    backgroundColor: "#f8d955",
    paddingHorizontal: 30,
    paddingVertical: 10,
    borderRadius: 10,
  },
  errorButtonText: {
    color: "#540f09",
    fontSize: 16,
    fontWeight: "bold",
  },
});

export default MiniGameModal;
